import jsPDF from "jspdf";

import jetBrainsMonoBoldUrl from "@/assets/fonts/JetBrainsMono-Bold.ttf?url";
import jetBrainsMonoRegularUrl from "@/assets/fonts/JetBrainsMono-Regular.ttf?url";
import outfitRegularUrl from "@/assets/fonts/Outfit-Regular.ttf?url";
import outfitSemiBoldUrl from "@/assets/fonts/Outfit-SemiBold.ttf?url";
import logoWhiteUrl from "@/assets/svg/logo-white.svg";
import { canvasToPngBlob, loadImage } from "@/shared/utils/canvasDrawing";
import {
  RECEIPT_FALLBACK_FONTS,
  RECEIPT_FONTS,
  registerReceiptFonts,
} from "@/shared/utils/paymentReceipt/receiptBrand";

import {
  paintTransferReceipt,
  RECEIPT_WIDTH,
  receiptHeight,
  type ReceiptPainter,
  type ReceiptTextStyle,
} from "./paintTransferReceipt";
import type { TransferReceiptModel } from "./transferReceipt";

// Outfit and JetBrains Mono in both formats — the TTFs jsPDF needs — so the image and the PDF measure alike.
const CANVAS_SANS = "ColocaReceiptSans";
const CANVAS_MONO = "ColocaReceiptMono";

let canvasFonts: Promise<boolean> | null = null;

function loadCanvasFonts(): Promise<boolean> {
  canvasFonts ??= Promise.all([
    new FontFace(CANVAS_SANS, `url(${outfitRegularUrl})`, { weight: "400" }).load(),
    new FontFace(CANVAS_SANS, `url(${outfitSemiBoldUrl})`, { weight: "600" }).load(),
    new FontFace(CANVAS_MONO, `url(${jetBrainsMonoRegularUrl})`, { weight: "400" }).load(),
    new FontFace(CANVAS_MONO, `url(${jetBrainsMonoBoldUrl})`, { weight: "700" }).load(),
  ])
    .then((faces) => {
      faces.forEach((face) => document.fonts.add(face));
      return true;
    })
    .catch(() => {
      canvasFonts = null;
      return false;
    });
  return canvasFonts;
}

const canvasFont = ({ font, bold, size }: ReceiptTextStyle) =>
  font === "mono"
    ? `${bold ? 700 : 400} ${size}px ${CANVAS_MONO}, ui-monospace, monospace`
    : `${bold ? 600 : 400} ${size}px ${CANVAS_SANS}, system-ui, sans-serif`;

function canvasPainter(ctx: CanvasRenderingContext2D, logo: HTMLImageElement | null): ReceiptPainter {
  const roundedPath = (x: number, y: number, w: number, h: number, r: number, topOnly: boolean) => {
    const bottom = topOnly ? 0 : r;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - bottom);
    ctx.quadraticCurveTo(x + w, y + h, x + w - bottom, y + h);
    ctx.lineTo(x + bottom, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - bottom);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };
  return {
    fillRect(x, y, w, h, color) {
      ctx.fillStyle = color;
      ctx.fillRect(x, y, w, h);
    },
    fillRoundedRect(x, y, w, h, r, color, topOnly = false) {
      roundedPath(x, y, w, h, r, topOnly);
      ctx.fillStyle = color;
      ctx.fill();
    },
    fillCircle(x, y, r, color) {
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
    },
    line(x1, y1, x2, y2, color, width, dash) {
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.setLineDash(dash ?? []);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.setLineDash([]);
    },
    text(value, x, y, style) {
      ctx.font = canvasFont(style);
      ctx.fillStyle = style.color;
      ctx.textAlign = style.align;
      ctx.textBaseline = "middle";
      ctx.fillText(value, x, y);
    },
    measure(value, style) {
      ctx.font = canvasFont(style);
      return ctx.measureText(value).width;
    },
    logo(x, y, w, h) {
      if (logo) ctx.drawImage(logo, x, y, w, h);
    },
  };
}

const logoAspect = (logo: HTMLImageElement | null) =>
  logo && logo.naturalWidth && logo.naturalHeight ? logo.naturalWidth / logo.naturalHeight : null;

export async function renderTransferReceiptImage(model: TransferReceiptModel): Promise<Blob> {
  const [, logo] = await Promise.all([loadCanvasFonts(), loadImage(logoWhiteUrl)]);
  const measuring = document.createElement("canvas").getContext("2d");
  if (!measuring) throw new Error("Canvas 2D is not available");
  const height = receiptHeight(canvasPainter(measuring, null), model);

  const canvas = document.createElement("canvas");
  canvas.width = RECEIPT_WIDTH;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D is not available");
  paintTransferReceipt(canvasPainter(ctx, logo), model, logoAspect(logo));
  return canvasToPngBlob(canvas);
}

/** Layout units → PDF points: the 1080-wide page prints 540 pt wide (7.5 in). */
const PT = 0.5;

let logoPng: Promise<{ dataUrl: string; aspect: number } | null> | null = null;

// JsPDF cannot place an SVG: the same white wordmark the image uses, rasterized at 4× its printed size.
function whiteLogoPng(): Promise<{ dataUrl: string; aspect: number } | null> {
  logoPng ??= loadImage(logoWhiteUrl).then((image) => {
    const aspect = logoAspect(image);
    if (!image || !aspect) return null;
    const canvas = document.createElement("canvas");
    canvas.width = 1520;
    canvas.height = Math.round(1520 / aspect);
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    return { dataUrl: canvas.toDataURL("image/png"), aspect };
  });
  return logoPng;
}

function pdfPainter(doc: jsPDF, fonts: boolean, logo: { dataUrl: string } | null): ReceiptPainter {
  const family = (style: ReceiptTextStyle) =>
    fonts ? RECEIPT_FONTS[style.font] : RECEIPT_FALLBACK_FONTS[style.font];
  const setText = (style: ReceiptTextStyle) => {
    doc.setFont(family(style), style.bold ? "bold" : "normal");
    doc.setFontSize(style.size * PT);
  };
  return {
    fillRect(x, y, w, h, color) {
      doc.setFillColor(color);
      doc.rect(x * PT, y * PT, w * PT, h * PT, "F");
    },
    fillRoundedRect(x, y, w, h, r, color, topOnly = false) {
      doc.setFillColor(color);
      doc.roundedRect(x * PT, y * PT, w * PT, h * PT, r * PT, r * PT, "F");
      if (topOnly) doc.rect(x * PT, (y + h - r) * PT, w * PT, r * PT, "F");
    },
    fillCircle(x, y, r, color) {
      doc.setFillColor(color);
      doc.circle(x * PT, y * PT, r * PT, "F");
    },
    line(x1, y1, x2, y2, color, width, dash) {
      doc.setDrawColor(color);
      doc.setLineWidth(width * PT);
      doc.setLineDashPattern(dash ? dash.map((step) => step * PT) : [], 0);
      doc.line(x1 * PT, y1 * PT, x2 * PT, y2 * PT);
      doc.setLineDashPattern([], 0);
    },
    text(value, x, y, style) {
      setText(style);
      doc.setTextColor(style.color);
      doc.text(value, x * PT, y * PT, { align: style.align, baseline: "middle" });
    },
    measure(value, style) {
      setText(style);
      return doc.getTextWidth(value) / PT;
    },
    logo(x, y, w, h) {
      if (logo) doc.addImage(logo.dataUrl, "PNG", x * PT, y * PT, w * PT, h * PT);
    },
  };
}

async function newReceiptPdf(height: number): Promise<{ doc: jsPDF; fonts: boolean }> {
  const doc = new jsPDF({ unit: "pt", format: [RECEIPT_WIDTH * PT, height * PT], compress: true });
  return { doc, fonts: await registerReceiptFonts(doc) };
}

export async function renderTransferReceiptPdf(model: TransferReceiptModel): Promise<Blob> {
  const logo = await whiteLogoPng().catch(() => null);
  // The page is as long as the receipt: measured on a scratch document with the same fonts.
  const scratch = await newReceiptPdf(1000);
  const height = receiptHeight(pdfPainter(scratch.doc, scratch.fonts, null), model);

  const { doc, fonts } = await newReceiptPdf(height);
  doc.setProperties({ title: model.title });
  paintTransferReceipt(pdfPainter(doc, fonts, logo), model, logo?.aspect ?? null);
  return doc.output("blob");
}
